import React, { useEffect, useMemo, useState, useRef } from 'react';
import { WaitingOrchestratorProps, DEFAULT_WAITING_CONFIG } from './types';
import { useWaitingState } from './hooks/useWaitingState';
import { BottomBar } from './BottomBar/BottomBar';
import { ReelsStream } from './ReelsStream/ReelsStream';
import { ChatOfferCard } from './Cards/ChatOfferCard';
import { HighlightCard } from './Cards/HighlightCard';
import { TopNavBar, WaitingTab } from './TopNavBar/TopNavBar';
import { MyCombinations } from './Tabs/MyCombinations';
import { UserProfile } from './Tabs/UserProfile';
import './WaitingOrchestrator.css';

const IntroOverlay: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [showButtons, setShowButtons] = useState(false);
  const fullText = "Bu ürün size çok yakışacak... Biz videoyu üretirken istersen küçültebilirsin, hazır olunca haber vereceğiz.";
  
  // Use a ref to keep track of the current callback without triggering effect re-runs
  const onCompleteRef = useRef(onComplete);
  
  // Update ref when prop changes
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    let index = 0;
    const intervalId = setInterval(() => {
      // Use fullText slicing instead of prev state to avoid any accumulation errors
      if (index >= fullText.length) {
        clearInterval(intervalId);
        setTimeout(() => {
          setShowButtons(true); // Show buttons after typing completes
        }, 500);
        return;
      }
      
      const nextText = fullText.slice(0, index + 1);
      setDisplayedText(nextText);
      index++;
    }, 20); // Typing speed

    return () => clearInterval(intervalId);
  }, []); // Empty dependency array ensures this effect runs ONLY ONCE on mount

  const handleWaitClick = () => {
    onCompleteRef.current();
  };

  const handleNotifyClick = () => {
    // Send minimize message to parent SDK
    window.parent.postMessage({ type: 'MINIMIZE_WIDGET' }, '*');
    onCompleteRef.current();
  };

  return (
    <div className="waiting-intro-overlay">
      <div className="waiting-intro-overlay__content">
        <p className="waiting-intro-overlay__text">{displayedText}</p>
        {showButtons && (
          <div className="intro-buttons">
            <button className="intro-btn intro-btn--wait" onClick={handleWaitClick}>
              Bekle
            </button>
            <button className="intro-btn intro-btn--notify" onClick={handleNotifyClick}>
              Hazır Olunca Haber Ver
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const WaitingOrchestrator: React.FC<WaitingOrchestratorProps> = ({
  brandId,
  waitingType,
  productSku,
  userId,
  onComplete,
  onResultReady,
  checkResultReady,
  onCreateNewAvatar,
  userMetadata
}) => {
  const [showIntro, setShowIntro] = useState(true);
  const [activeTab, setActiveTab] = useState<WaitingTab>('product');
  const orchestratorRef = useRef<HTMLDivElement>(null);

  // Request fullscreen on mount to hide mobile browser navbar
  useEffect(() => {
    const enterFullscreen = async () => {
      try {
        const elem = orchestratorRef.current;
        if (!elem) return;

        // Request fullscreen for iOS Safari and other browsers
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if ((elem as any).webkitRequestFullscreen) {
          // iOS Safari
          await (elem as any).webkitRequestFullscreen();
        } else if ((elem as any).webkitEnterFullscreen) {
          // iOS video element
          await (elem as any).webkitEnterFullscreen();
        }
        
        console.log('✅ Fullscreen mode activated');
      } catch (error) {
        console.log('ℹ️ Fullscreen not available or blocked:', error);
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(enterFullscreen, 100);
    return () => clearTimeout(timer);
  }, []);

  // Exit fullscreen when component unmounts
  useEffect(() => {
    return () => {
      if (document.fullscreenElement || (document as any).webkitFullscreenElement) {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          (document as any).webkitExitFullscreen();
        }
      }
    };
  }, []);

  const {
    state,
    sessionId,
    config,
    progressPercent,
    elapsedSeconds,
    reels,
    currentReelIndex,
    startSession,
    handleContinueClick,
    nextReel,
    prevReel,
    trackEvent,
    isLoading,
    error,
    sessionMetadata,
    isResultReady // Used to check if result came back immediately
  } = useWaitingState({
    brandId,
    waitingType,
    productSku,
    userId,
    checkResultReady,
    onResultReady
  });

  useEffect(() => {
    // If result is ready immediately (e.g. cached), skip intro and show result
    if (isResultReady) {
      setShowIntro(false);
      setActiveTab('my_combinations');
    }
  }, [isResultReady]);

  useEffect(() => {
    startSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state === 'COMPLETED') {
      onComplete();
    }
  }, [state, onComplete]);

  const activeConfig = config || DEFAULT_WAITING_CONFIG;

  const shouldShowCard = useMemo(() => {
    if (!activeConfig.modules || reels.length === 0) {
      return { chat: false, highlight: false };
    }

    const currentProgress = currentReelIndex / Math.max(reels.length - 1, 1);
    const currentPct = currentProgress * 100;

    return {
      highlight: activeConfig.modules.highlight && currentPct >= activeConfig.triggers.highlight_pct,
      chat: activeConfig.modules.chat_offer && currentPct >= activeConfig.triggers.chat_pct
    };
  }, [activeConfig, currentReelIndex, reels.length]);

  const getProductName = () => {
    switch (waitingType) {
      case 'fashplay': return 'FashPlay';
      case 'fashsnap': return 'FashSnap';
      case 'fashmatch': return 'FashMatch';
      default: return 'Ürün';
    }
  };

  if (isLoading) {
    return (
      <div className="waiting-orchestrator waiting-orchestrator--loading">
        <div className="waiting-orchestrator__loader">
          <div className="waiting-orchestrator__spinner" />
          <p>Deneyim hazırlanıyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="waiting-orchestrator waiting-orchestrator--error">
        <div className="waiting-orchestrator__error-content">
          <p>{error}</p>
          <button onClick={startSession}>Tekrar Dene</button>
        </div>
      </div>
    );
  }

  return (
    <div className="waiting-orchestrator" ref={orchestratorRef}>
      {showIntro && <IntroOverlay onComplete={() => setShowIntro(false)} />}
      
      <TopNavBar 
        activeTab={activeTab} 
        onTabChange={setActiveTab} 
        productName={getProductName()}
      />

      <div className="waiting-orchestrator__content">
        {activeTab === 'product' && (
          <>
            {(activeConfig.modules.reels || activeConfig.modules.brand_reels) && reels.length > 0 ? (
              <ReelsStream
                reels={reels}
                currentIndex={currentReelIndex}
                onNext={nextReel}
                onPrev={prevReel}
                sessionId={sessionId}
                trackEvent={trackEvent}
              />
            ) : (
              <div className="waiting-orchestrator__fallback">
                <div className="waiting-orchestrator__fallback-content">
                  <div className="waiting-orchestrator__pulse-ring" />
                  <div className="waiting-orchestrator__logo">F</div>
                  <p className="waiting-orchestrator__message">
                    {state === 'GENERATING' 
                      ? 'Stiliniz hazırlanıyor...' 
                      : 'Sonucunuz hazır!'}
                  </p>
                </div>
              </div>
            )}

            <div className="waiting-orchestrator__cards">

              {shouldShowCard.highlight && (
                <HighlightCard />
              )}

              {shouldShowCard.chat && (
                <ChatOfferCard />
              )}
            </div>
          </>
        )}

        {activeTab === 'my_combinations' && (
          <MyCombinations userId={userId || ''} />
        )}

        {activeTab === 'profile' && (
          <UserProfile 
            metadata={userMetadata || sessionMetadata} 
            avatarUrl={userMetadata?.avatarUrl || (typeof sessionMetadata?.avatarUrl === 'string' ? sessionMetadata.avatarUrl : undefined)}
            onCreateNewAvatar={onCreateNewAvatar}
          />
        )}
      </div>

      <BottomBar
        state={state}
        progressPercent={progressPercent}
        elapsedSeconds={elapsedSeconds}
        estimatedDuration={activeConfig.estimated_duration_seconds}
        onContinueClick={handleContinueClick}
      />
    </div>
  );
};

export default WaitingOrchestrator;
